// UI strings of "Ääntämisen apu äänityksissä", the coach's features in our own recordings (app/src/coach/rec/;
// docs/design/coach-research/recordings-plan.md Phase 1; engine/src/coach/rec-prefs.ts): the settings group in
// Asetukset → Harjoittelu, the sound hint after a take, the articulation card, the mouth replay, the partner's
// one-letter question, the better-take line, the sound focus and the agreement table. Style: docs/FINNISH-STYLE.md
// (sinä-muoto, no jargon, no intro text). Letters and IPA in the lines are passed in, never written here.
import type { RecCoachKey } from '@valyrio/engine/coach/rec-prefs';
import { defineArea } from '../area';

export const recCoachFi = {
  /** The settings group (Asetukset → Harjoittelu). */
  title: 'Ääntämisen apu äänityksissä',
  features: {
    clean: {
      label: 'Puhdas otto',
      note: 'Äänität ilman puhelimen äänenkäsittelyä, jotta äänesi kuuluu sellaisenaan. Otto tallentuu kuten ennenkin.',
    },
    hint: {
      label: 'Äännevihje',
      note: 'Oton jälkeen yksi kirjain voidaan alleviivata pisteviivalla, jos mittari ja puhemalli kuulevat saman. Muuten vihjettä ei näy.',
    },
    card: {
      label: 'Näin se sanotaan',
      note: 'Äänitysnapin yläpuolella suun kuva, jonka voit avata, ja Petersonin ääntämys hidastettuna.',
    },
    replay: {
      label: 'Suukuva otosta',
      note: 'Kun napautat vihjeen kirjainta, suun kuva näyttää tavoitteen ja arvion siitä, miten äänsit.',
    },
    better: {
      label: 'Parempi otto',
      note: 'Kun äänität lauseita, saat huomaamattoman vihjeen, jos toisen oton äänteet olivat lähempänä Petersonia. Valitset itse.',
    },
    focus: {
      label: 'Harjoitellaan ääntä',
      note: 'Jos sanan äänne ei onnistu kahtena päivänä, sana lisätään harjoiteltaviin. Se poistuu kahden onnistuneen oton jälkeen.',
    },
    earAsk: {
      label: 'Kysy minulta kumppanin otoista',
      note: 'Enintään kolme kysymystä päivässä, kukin yhdestä kirjaimesta. Voit ohittaa ne. Et näe, mitä sovellus kuuli.',
    },
    earMine: {
      label: 'Kumppani saa arvioida ottojani',
      note: 'Kumppanin arvioista selviää, kuuleeko sovellus äänteesi oikein.',
    },
  } satisfies Record<RecCoachKey, { label: string; note: string }>,
  /** The partner's ear: the two sides as one sub-heading. */
  earGroup: 'Kumppanin korva',
  /** A feature whose need is off: "Tarvitsee: Puhdas otto". */
  needs: (name: string) => `Tarvitsee: ${name}`,
  model:
    'Äännevihje tarvitsee puhemallin tähän laitteeseen (noin 70 Mt). Jos puhemalli ei toimi laitteessasi, vihje kertoo vain, mitä mittari havaitsi, eikä arvioi ääntämistäsi.',
  modelLink: 'Lataa puhemalli',
  calibration: 'Mittarit ovat tarkempia, kun äänesi on kalibroitu tällä laitteella.',
  calibrationLink: 'Kalibroi äänesi',
  focusGate: 'Äänne otetaan mukaan vasta, kun kumppanisi ja sovellus ovat olleet siitä tarpeeksi usein samaa mieltä (vähintään 20 arviota).',

  hint: {
    /** The underlined letter's line: "j kuulosti ž:ltä". `heard` is the letter with its case ending ("ž:ltä"). */
    heard: (letter: string, heard: string) => `${letter} kuulosti ${heard}`,
    /** A near verdict without a clear competitor. */
    near: (letter: string) => `${letter} oli melkein oikein`,
    /** Peterson's own phone beside it: "Peterson: selvä dʒ". */
    peterson: (ipa: string) => `Peterson: selvä ${ipa}`,
    /** rh, lj and q: no model audio of Peterson's to judge by. */
    noModel: (letter: string) => `${letter}: ei Petersonin mallia · en ole varma`,
    /** Meters only (no model on this device): a measurement, never a verdict. */
    meterOnly: (letter: string, what: string) => `${letter}: ${what} · vain mittari, en ole varma`,
    /** What a meter measured, by its fix id (short, neutral). */
    measured: {
      'keep-buzz': 'soinnillisuus katkesi',
      'add-buzz': 'soinnillisuutta ei kuulunut',
      'less-buzz': 'soinnillisuutta kuului',
      'tip-forward': 'sihinä kuulosti š:ltä',
      'tip-back': 'sihinä kuulosti s:ltä',
      'add-stop': 'sulkua ei kuulunut',
      'shorter-hiss': 'sihinä oli pitkä',
      'no-gap': 'kuului täysi sulku',
      'further-back': 'kuulosti k:lta',
      'one-sound': 'kuului kaksi äännettä',
      'more-taps': 'kuului yksi napautus',
      'round-lips': 'huulet eivät pyöristyneet',
      'stress-later': 'paino oli alussa',
      'more-melody': 'sävelkulku oli tasainen',
      'louder-stress': 'painollinen tavu ei erottunut',
    } as Record<string, string>,
    you: 'Sinä',
    petersonBar: 'Peterson',
    /** The take was processed by the phone after all (iOS ignored the request). */
    processed: 'Puhelin käsitteli äänen silti, joten en luota tämän oton vihjeisiin.',
    /** The polite live summary, once per take. */
    live: (line: string) => `Äännevihje: ${line}`,
    openReplay: 'Näytä suukuva',
  },

  replay: {
    title: (letter: string) => `Suukuva: ${letter}`,
    target: 'Tavoite',
    yours: 'Sinun ottosi',
    playTarget: 'Toista Petersonin ääntämys',
    playYours: 'Toista ottosi 0,75×',
    unsure: 'En ole varma, miten kielesi liikkui.',
    close: 'Sulje',
  },

  card: {
    title: (letter: string) => `Näin se sanotaan: ${letter}`,
    next: 'Seuraava vaihe',
    step: (n: number, of: number) => `Vaihe ${n}/${of}`,
    slow: 'Peterson 0,75×',
    noClip: 'Lauseen sisällä, ei erillistä mallia.',
  },

  ear: {
    ask: 'Miltä kuulosti?',
    ok: 'Oikein',
    near: 'Melkein',
    no: 'Väärin',
    skip: 'Ohita',
  },

  better: (n: number, letters: string) => `Otto ${n}: ${letters} lähempänä Petersonia`,

  focus: {
    label: 'Harjoitellaan ääntä',
  },

  agreement: {
    title: 'Kumppanin korva ja sovellus',
    sound: 'Äänne',
    n: 'Arvioita',
    kappa: 'Yhtäpitävyys',
    range: 'Vaihteluväli',
    state: 'Sovellus',
    graded: 'arvioi',
    measured: 'vain mittaa',
    none: 'Ei vielä arvioita.',
    rule: 'Äänne arvioidaan vasta, kun arvioita on vähintään 20 ja yhtäpitävyys on vähintään 0,6.',
  },
};

export const recCoachEn: typeof recCoachFi = {
  title: 'Pronunciation help in recordings',
  features: {
    clean: {
      label: 'Clean take',
      note: 'You record without the phone’s voice processing, so your voice is heard as it is. The take is saved as before.',
    },
    hint: {
      label: 'Sound hint',
      note: 'After a take one letter may get a dotted underline, when the meter and the speech model heard the same. Otherwise no hint shows.',
    },
    card: {
      label: 'How it’s said',
      note: 'A collapsible picture of the mouth above the record button, with Peterson saying it slowly.',
    },
    replay: {
      label: 'Mouth replay',
      note: 'Tap the hint’s letter: the mouth picture shows the target and an estimate of how you said it.',
    },
    better: {
      label: 'Better take',
      note: 'When voicing sentences, a quiet line if the other take’s sounds were closer. You choose.',
    },
    focus: {
      label: 'Sound practice',
      note: 'A word whose sound was off on two days is practised, and leaves after two good takes.',
    },
    earAsk: {
      label: 'Ask me about my partner’s takes',
      note: 'At most three one-letter questions a day, all skippable. You don’t see what the app heard.',
    },
    earMine: {
      label: 'My partner may judge my takes',
      note: 'Your partner’s answers show whether the app hears your sounds right.',
    },
  },
  earGroup: 'Partner’s ear',
  needs: (name) => `Needs: ${name}`,
  model:
    'The sound hint needs the speech model on this device (about 70 MB). If the model doesn’t run on your device, the hint gives only the meter’s finding, never a verdict.',
  modelLink: 'Download the speech model',
  calibration: 'The meters are more accurate when your voice is calibrated on this device.',
  calibrationLink: 'Calibrate your voice',
  focusGate: 'A sound counts only once your partner’s answers and the app have agreed often enough (at least 20 answers).',
  hint: {
    heard: (letter, heard) => `${letter} sounded like ${heard}`,
    near: (letter) => `${letter} was nearly right`,
    peterson: (ipa) => `Peterson: a clear ${ipa}`,
    noModel: (letter) => `${letter}: no model from Peterson · not sure`,
    meterOnly: (letter, what) => `${letter}: ${what} · meter only, not sure`,
    measured: {
      'keep-buzz': 'the voicing stopped',
      'add-buzz': 'no voicing heard',
      'less-buzz': 'voicing heard',
      'tip-forward': 'the hiss sounded like š',
      'tip-back': 'the hiss sounded like s',
      'add-stop': 'no closure heard',
      'shorter-hiss': 'the hiss was long',
      'no-gap': 'a full closure heard',
      'further-back': 'sounded like k',
      'one-sound': 'two sounds heard',
      'more-taps': 'one tap heard',
      'round-lips': 'the lips did not round',
      'stress-later': 'the stress was at the start',
      'more-melody': 'the pitch was flat',
      'louder-stress': 'the stressed syllable did not stand out',
    },
    you: 'You',
    petersonBar: 'Peterson',
    processed: 'The phone processed the sound anyway, so I don’t trust this take’s hints.',
    live: (line) => `Sound hint: ${line}`,
    openReplay: 'Show the mouth',
  },
  replay: {
    title: (letter) => `Mouth replay: ${letter}`,
    target: 'Target',
    yours: 'Your take',
    playTarget: 'Play Peterson',
    playYours: 'Play your take at 0.75×',
    unsure: 'I’m not sure what your tongue did.',
    close: 'Close',
  },
  card: {
    title: (letter) => `How it’s said: ${letter}`,
    next: 'Next phase',
    step: (n, of) => `Phase ${n}/${of}`,
    slow: 'Peterson 0.75×',
    noClip: 'Within a line, no separate model.',
  },
  ear: {
    ask: 'How did it sound?',
    ok: 'Right',
    near: 'Nearly',
    no: 'Wrong',
    skip: 'Skip',
  },
  better: (n, letters) => `Take ${n}: ${letters} closer to Peterson`,
  focus: {
    label: 'Sound practice',
  },
  agreement: {
    title: 'Partner’s ear and the app',
    sound: 'Sound',
    n: 'Answers',
    kappa: 'Agreement',
    range: 'Range',
    state: 'The app',
    graded: 'judges',
    measured: 'measures only',
    none: 'No answers yet.',
    rule: 'A sound is judged only with at least 20 answers and an agreement of at least 0.6.',
  },
};

// Messages.recCoach (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('recCoach', recCoachFi, recCoachEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof recCoachFi> {}
}
